import { useSelector, useSoftDeployAccess } from 'App/components/hooks';
import { Block, ClickableItem, DropDownMenu, IconName, SplitButtonWithActions } from '@upsales/components';
import Client from 'App/resources/Model/Client';
import { createActions, createMenuAvailable } from './actions';
import { createActionsWithoutClient, createMenuAvailableWithoutClient } from './actionsWithoutClient';
import { RootState } from 'Store/index';
import React from 'react';
import { PrimaryButton } from '@upsales/components/Buttons';
import { useTranslation } from 'Components/Helpers/translate';
import BemClass from '@upsales/components/Utils/bemClass';
import type { CreateMenuAvailable } from './actions';
import type { CreateMenuAvailableWithoutClient } from './actionsWithoutClient';

import './ClientCardSplitButton.scss';

export type CardCreateMenuAction = {
	id: string;
	icon: IconName;
	title: string;
	onClick: () => void;
	/** Rendered greyed out with `disabledTooltip` instead of being dropped from the menu. */
	disabled?: boolean;
	disabledTooltip?: string;
};

/**
 * The plain "Create" dropdown a card header hangs its create actions off.
 *
 * It lives in this file, and keeps this file's BEM block, so that the contact
 * card (ContactCardQuickCreate) inherits the dropdown's sizing and scroll rules
 * along with the markup — the same arrangement as ClientCardHeaderTab being
 * shared out of ClientCardHeader. Callers filter their own actions; this only
 * renders the ones it is handed.
 *
 * `buttonTitle` is what the trigger says. It defaults to the client card's
 * "Create"; the Angular contact card's button said "Add", which is what the
 * contact card passes.
 */
export const CardCreateMenu = ({
	actions,
	buttonTitle = 'default.create'
}: {
	actions: CardCreateMenuAction[];
	buttonTitle?: string;
}) => {
	const { t } = useTranslation();
	const classes = new BemClass('ClientCardSplitButton');

	return (
		<DropDownMenu
			useAnimation
			align="right"
			className={classes.b()}
			noMinWidth
			renderTrigger={(_, setExpanded) => (
				<Block space="mtm">
					<PrimaryButton onClick={setExpanded} icon="plus">
						{t(buttonTitle)}
					</PrimaryButton>
				</Block>
			)}
		>
			{close => (
				<>
					{actions.map(action => (
						<ClickableItem
							block
							key={action.id}
							icon={action.icon}
							color="black"
							disabled={action.disabled}
							titleTooltip={action.disabledTooltip ? t(action.disabledTooltip) : undefined}
							title={t(action.title)}
							onClick={e => {
								e.stopPropagation();
								action.onClick();
								close();
							}}
						/>
					))}
				</>
			)}
		</DropDownMenu>
	);
};

type Props = {
	currentTab: string;
	client?: Client;
	score?: number;
	contactsTotal?: number;
};

const ClientCardSplitButton = (props: Props) => {
	let { currentTab, client, score, contactsTotal } = props;
	const accountSelf = useSelector(({ App }) => App.accountSelf);
	const { customerId } = useSelector(({ App }: RootState) => App);
	const hasActivities = !useSoftDeployAccess('REMOVE_ACTIVITIES');
	const classes = new BemClass('ClientCardSplitButton');

	if (!accountSelf) {
		return null;
	}

	if (!hasActivities && currentTab === 'activities') {
		currentTab = 'phonecalls';
	}

	const tabActions: (keyof CreateMenuAvailable)[] = [
		'contacts',
		'activities',
		'phonecalls',
		'opportunities',
		'orders',
		'relations',
		'market',
		'files',
		'support',
		'projectPlan'
	];
	let actions;
	let filteredActions: ReturnType<typeof createActions> | [];
	let hasCreateMenuAvailable;

	if (client) {
		const canCreateEntity: CreateMenuAvailable = createMenuAvailable(accountSelf, client);
		actions = createActions(accountSelf, client, customerId, score, contactsTotal);
		filteredActions = actions.filter(action => action.visible?.(accountSelf, client as Client) ?? true);
		hasCreateMenuAvailable = canCreateEntity[currentTab as keyof CreateMenuAvailable];
	} else {
		const canCreateEntity: CreateMenuAvailableWithoutClient = createMenuAvailableWithoutClient(accountSelf);
		actions = createActionsWithoutClient(accountSelf);
		filteredActions = actions.filter(action => action.visible?.(accountSelf) ?? true);
		hasCreateMenuAvailable = canCreateEntity[currentTab as keyof CreateMenuAvailableWithoutClient];
	}

	return tabActions.includes(currentTab as keyof CreateMenuAvailable) && hasCreateMenuAvailable ? (
		<SplitButtonWithActions
			className={classes.b()}
			actions={filteredActions.map(i => ({
				...i,
				title: i.title,
				onClick: () => {
					i.onClick();
				}
			}))}
			space="mtm"
			trigger="click"
			dropdownDirection="bottom"
			mainAction={currentTab}
			icon="plus"
		/>
	) : (
		<CardCreateMenu actions={filteredActions} />
	);
};

export default ClientCardSplitButton;
